import { useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Skeleton,
  Pagination,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconCash, IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { usePayments, useDownloadPaymentReceipt } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

const PAGE_SIZE = 20;

export default function Payments() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePayments({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const downloadReceipt = useDownloadPaymentReceipt();

  const payments = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  const handleDownloadReceipt = (paymentId: number, paymentNumber: string) => {
    downloadReceipt.mutate(paymentId, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${paymentNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => {
        notifications.show({
          title: 'Download failed',
          message: 'Unable to download receipt. Please try again.',
          color: 'red',
        });
      },
    });
  };

  return (
    <Stack gap="lg">
      <PageHeader title="Payment History" description="View all payments made on your account" />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : payments.length === 0 ? (
        <EmptyState
          icon={<IconCash size={30} />}
          title="No payments found"
          description="No payments have been recorded yet"
        />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Payment #</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Invoice</Table.Th>
                  <Table.Th>Method</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                  <Table.Th>Reference</Table.Th>
                  <Table.Th w={60} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {payments.map((pmt) => (
                  <Table.Tr key={pmt.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{pmt.payment_number}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{dayjs(pmt.paid_at).format('MMM D, YYYY')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{pmt.invoice_number ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" tt="capitalize">{pmt.method.replace(/_/g, ' ')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={pmt.status} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={500}>${parseFloat(pmt.amount).toFixed(2)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{pmt.reference ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Download receipt">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleDownloadReceipt(pmt.id, pmt.payment_number)}
                          loading={downloadReceipt.isPending}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={page} onChange={setPage} />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
